import { useState, useEffect, useCallback } from "react";
import { LoadPanel } from "devextreme-react";
import Splitter, { Item as SplitterItem } from "devextreme-react/splitter";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { getRoles } from "../../../api/support";
import { CustomDataGrid } from "../CustomDataGrid";
import { RoleInfoPane } from "./RoleInfoPane";
import { RoleRow, MOCK_ROLES } from "./roles-mock";
import "../messages/TicketPage.css";

// Roller — OrganizationsPage/TeamsPage duzeni: ust panelde liste, alt panelde
// cok-sekmeli (MDI) rol detayi (yetki matrisi).
type RoleTab = { ref: string; roleRef: string; title: string };
const createTabId = () => `rtab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function RolesPage() {
    const { t } = useTranslation();

    const [roles, setRoles] = useState<RoleRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [gridKey, setGridKey] = useState(0);

    const [tabs, setTabs] = useState<RoleTab[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [topCollapsed, setTopCollapsed] = useState(false);
    const [bottomCollapsed, setBottomCollapsed] = useState(false);

    const activeTab = tabs.find(tb => tb.ref === activeTabId) || null;

    const toggleBottom = useCallback(() => {
        setBottomCollapsed(prev => !prev);
        setTopCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    const toggleTop = useCallback(() => {
        setTopCollapsed(prev => !prev);
        setBottomCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    // ── Load roles ────────────────────────────────────────────────────────────
    // Backend getRoles() bos donerse mock fallback'a duser; dolu donerse ad
    // eslestirilip mock yetki/uye verisiyle zenginlestirilir.
    const load = useCallback(() => {
        setLoading(true);
        getRoles(false)
            .then(list => {
                if (list.length) {
                    setRoles(list.map(r => {
                        const mock = MOCK_ROLES.find(m => m.name.toLowerCase() === r.name.toLowerCase());
                        return { ...r, userCount: mock?.userCount ?? 0 } as RoleRow;
                    }));
                } else {
                    setRoles(MOCK_ROLES);
                }
            })
            .catch(() => setRoles(MOCK_ROLES))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Tab yonetimi ──────────────────────────────────────────────────────────
    const openRoleInTab = useCallback((role: RoleRow) => {
        setTabs(prev => {
            const existing = prev.find(tb => tb.roleRef === String(role.ref));
            if (existing) { setActiveTabId(existing.ref); return prev; }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, { ref: tabId, roleRef: String(role.ref), title: role.display_name || role.name || `#${role.ref}` }];
        });
    }, []);

    const closeTab = useCallback((tabId: string) => {
        setTabs(prev => {
            if (prev.length <= 1) { setActiveTabId(null); return []; }
            const idx = prev.findIndex(tb => tb.ref === tabId);
            const next = prev.filter(tb => tb.ref !== tabId);
            setActiveTabId(cur => cur === tabId ? (next[Math.max(0, idx - 1)] || next[0])?.ref || null : cur);
            return next;
        });
    }, []);

    const selectRole = useCallback((role: RoleRow) => {
        if (!activeTabId || tabs.length === 0) { openRoleInTab(role); return; }
        setTabs(prev => prev.map(tb => tb.ref === activeTabId
            ? { ...tb, roleRef: String(role.ref), title: role.display_name || role.name || `#${role.ref}` }
            : tb));
    }, [activeTabId, tabs.length, openRoleInTab]);

    const handleRowClick = (data: RoleRow) => selectRole(data);
    const handleFocusedRowChanged = useCallback((e: any) => {
        if (e?.row?.data) selectRole(e.row.data);
    }, [selectRole]);

    const columns = [
        { dataField: "display_name", caption: t("roleName"), width: 200 },
        {
            dataField: "name", caption: t("code"), width: 150,
            cellRender: (d: any) => <span className="font-mono text-xs" style={{ color: "var(--subtitle-text-color)" }}>{d.value}</span>,
        },
        { dataField: "description", caption: t("description"), width: 300 },
        { dataField: "userCount", caption: t("users"), width: 100, alignment: "center" as const },
        {
            dataField: "is_active", caption: t("status"), width: 110, alignment: "center" as const,
            cellRender: (d: any) => {
                const active = d.value !== false;
                const color = active ? "#10B981" : "#9CA3AF";
                return (
                    <span style={{ background: `${color}1f`, color, fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999 }}>
                        {active ? t("active") : t("passive")}
                    </span>
                );
            },
        },
        { dataField: "", caption: "", width: undefined, allowSorting: false, allowFiltering: false },
    ];

    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            <div className="ticket-splitter-wrap flex-1 min-h-0">
                <Splitter orientation="vertical">
                    {/* Üst Pane - Rol listesi */}
                    <SplitterItem size="40%" minSize="200px" collapsed={topCollapsed}>
                        <CustomDataGrid
                            key={`roles-grid-${gridKey}`}
                            className="custom-grid compact-grid"
                            data={roles}
                            columns={columns}
                            keyExpr="ref"
                            height="100%"
                            t={t}
                            noDataText={t("noDataText")}
                            onRefresh={load}
                            onRowClick={handleRowClick}
                            onFocusedRowChanged={handleFocusedRowChanged}
                            focusedRowKey={activeTab?.roleRef ?? undefined}
                            columnChooserEnabled
                            filterRowVisible={false}
                            rowAlternationEnabled={true}
                            allowColumnReordering
                            exportEnabled
                            additionalToolbarItems={[
                                {
                                    location: "after",
                                    render: () => (
                                        <button type="button" className="splitter-collapse-btn" title={t("closeBottomPane")} onClick={toggleBottom}>
                                            <FontAwesomeIcon icon={bottomCollapsed ? faChevronUp : faChevronDown} />
                                        </button>
                                    ),
                                },
                            ]}
                        />
                    </SplitterItem>

                    {/* Alt Pane - Sekmeli rol detayi */}
                    <SplitterItem size="60%" minSize="300px" collapsed={bottomCollapsed}>
                        <div className="h-full flex flex-col overflow-hidden" style={{ background: "var(--background-color)", padding: "0 12px" }}>
                            {tabs.length > 0 ? (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        {tabs.map(tab => (
                                            <div
                                                key={tab.ref}
                                                className={`detail-header-tab ${tab.ref === activeTabId ? "active" : ""}`}
                                                title={tab.title}
                                                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(tab.ref); } }}
                                            >
                                                <button type="button" className="detail-header-tab-label" onClick={() => setActiveTabId(tab.ref)}>
                                                    <i className="dx-icon-key detail-header-tab-icon" />
                                                    <span className="detail-header-tab-title-text">{tab.title}</span>
                                                </button>
                                                <button type="button" className="detail-header-tab-close" onClick={(e) => { e.stopPropagation(); closeTab(tab.ref); }} title={t("closeTab")}>
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        <button type="button" className="splitter-collapse-btn" style={{ marginLeft: "auto" }} title={t("closeTopPane")} onClick={toggleTop}>
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>

                                    <div className="flex-1 min-h-0">
                                        {tabs.map(tab => (
                                            <div key={tab.ref} className="h-full" style={{ display: tab.ref === activeTabId ? "block" : "none" }}>
                                                <RoleInfoPane roleRef={tab.roleRef} />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        <button type="button" className="splitter-collapse-btn" style={{ marginLeft: "auto" }} title={t("closeTopPane")} onClick={toggleTop}>
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-center flex-1" style={{ color: "var(--subtitle-text-color)" }}>
                                        {t("selectRoleForDetail")}
                                    </div>
                                </>
                            )}
                        </div>
                    </SplitterItem>
                </Splitter>
            </div>
        </div>
    );
}
